import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useRouter } from 'expo-router'
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { captureException, errorToString } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { newRecordId, pb, useStore } from '@/lib/pocketbase'
import type { UserRole } from '@/lib/types'

interface LoggedInInviteState {
    isLoading: boolean
    error: string | null
    isWrongRecipient: boolean
    showSignupOption: boolean
    invitedEmail: string | null
}

export const useLoggedInInviteAcceptance = (invitationId: string) => {
    const router = useRouter()
    const [invitationsCollection, userOrgCollection] = useStore('invitations', 'user_org')
    const [state, setState] = useState<LoggedInInviteState>({
        isLoading: false,
        error: null,
        isWrongRecipient: false,
        showSignupOption: false,
        invitedEmail: null,
    })

    const { user } = useAuth()

    const { data: invitations = [] } = useLiveQuery(
        q =>
            invitationId
                ? q
                      .from({ invitations: invitationsCollection })
                      .where(({ invitations }) => eq(invitations.id, invitationId))
                : undefined,
        [invitationId]
    )

    const invitation = invitations[0] ?? null

    const acceptInvitationMutation = useMutation({
        mutationFn: async ({
            userId,
            invitationToAccept,
        }: {
            userId: string
            invitationToAccept: typeof invitation
        }) => {
            if (!invitationToAccept) throw new Error('Invitation not found')

            if (invitationToAccept.email !== user?.email) {
                setState(prev => ({
                    ...prev,
                    isWrongRecipient: true,
                    invitedEmail: invitationToAccept.email,
                    error: 'This invitation was sent to a different email address.',
                }))
                throw new Error('Email mismatch')
            }

            await performMutations(function* () {
                yield invitationsCollection.update(invitationId, draft => {
                    draft.used = true
                })
                yield userOrgCollection.insert({
                    id: newRecordId(),
                    user: userId,
                    org: invitationToAccept.org,
                    role: (invitationToAccept.role || 'workforce') as UserRole,
                    working_hours_override: false,
                    work_start_time: '',
                    work_end_time: '',
                    working_days: null,
                    color: '',
                    tracking_paused_until: '',
                    inactive_at: '',
                    supervisor: '',
                    clockin_reminders_disabled: false,
                })
            })

            const org = await pb.collection('orgs').getOne(invitationToAccept.org)
            return org
        },
        onSuccess: org => {
            router.push(`/${org.slug}`)
        },
    })

    const attemptAcceptInvitation = useCallback(async () => {
        if (!user || !invitationId || !invitation) return

        setState(prev => ({ ...prev, error: null }))

        try {
            await acceptInvitationMutation.mutateAsync({
                userId: user.id,
                invitationToAccept: invitation,
            })
        } catch (error) {
            captureException('Accept invitation while logged in', error)
            setState(prev => ({
                ...prev,
                error: errorToString(error, 'Failed to accept invitation'),
            }))
        }
    }, [user, invitationId, invitation, acceptInvitationMutation])

    const showSignupForm = useCallback(() => {
        setState(prev => ({ ...prev, showSignupOption: true }))
    }, [])

    const hideSignupForm = useCallback(() => {
        setState(prev => ({ ...prev, showSignupOption: false }))
    }, [])

    const logoutAndRefresh = useCallback(async () => {
        try {
            pb.authStore.clear()
            router.push('/auth/sign-in')
        } catch (_error) {}
    }, [router])

    useEffect(() => {
        if (
            user &&
            invitationId &&
            invitation &&
            !state.isWrongRecipient &&
            !state.showSignupOption
        ) {
            attemptAcceptInvitation()
        }
    }, [
        user,
        invitationId,
        invitation,
        attemptAcceptInvitation,
        state.isWrongRecipient,
        state.showSignupOption,
    ])

    return {
        isLoggedIn: !!user,
        ...state,
        isLoading: acceptInvitationMutation.isPending,
        showSignupForm,
        hideSignupForm,
        logoutAndRefresh,
        retryAcceptance: attemptAcceptInvitation,
    }
}
